import { useEffect, useState } from "react";
import SectionCard from "../components/SectionCard";
import { Label, Input, Select } from "../components/FormControls";
import ConfirmDialog from "../components/ConfirmDialog";
import { Percent } from "lucide-react";
import { toast } from "react-toastify";

import useOrderSettings from "../../../Services/OrderServices/OrderSettingsServices/useOrderSettings";

const formatOrderType = (value = "") => {
  const normalizedValue = String(value || "")
    .trim()
    .toLowerCase();

  const labels = {
    home_delivery: "Home Delivery",
    homedelivery: "Home Delivery",
    takeaway: "Takeaway",
    dining: "Dining",
    dinein: "Dining",
    room_order: "Room Order",
    office_order: "Office Order",
    carorder: "Car Order",
    car_order: "Car Order",
  };

  return (
    labels[normalizedValue] ||
    normalizedValue
      .replace(/_/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase()) ||
    "—"
  );
};

const formatCurrency = (value) => {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "₹0.00";
  }

  return `₹${amount.toFixed(2)}`;
};

export default function DiscountsSection({ userID }) {
  const {
    getDeliveryChargesAndDiscount,
    updateDiscount,
    deleteDiscount,
  } = useOrderSettings();

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    discount: "",
    fromAmount: "",
    toAmount: "",
    discountType: "",
    orderType: "",
    maxDiscount: "",
  });

  const [confirm, setConfirm] = useState({
    open: false,
    onConfirm: null,
    title: "",
    message: "",
  });

  const ask = (title, message, callback) => {
    setConfirm({
      open: true,
      title,
      message,
      onConfirm: async () => {
        setConfirm({
          open: false,
          onConfirm: null,
          title: "",
          message: "",
        });

        await callback();
      },
    });
  };

  const cancelConfirm = () => {
    setConfirm({
      open: false,
      onConfirm: null,
      title: "",
      message: "",
    });
  };

  const load = async () => {
    if (!userID) {
      setList([]);
      return;
    }

    setLoading(true);

    try {
      const result = await getDeliveryChargesAndDiscount(userID);

      if (result?.success) {
        setList(
          Array.isArray(result?.discount)
            ? result.discount
            : [],
        );
      } else {
        setList([]);

        toast.error(
          result?.message || "Failed to fetch discounts",
        );
      }
    } catch (error) {
      console.error("DISCOUNT LOAD ERROR:", error);

      setList([]);
      toast.error("Failed to fetch discounts");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    const {
      discount,
      fromAmount,
      toAmount,
      discountType,
      orderType,
      maxDiscount,
    } = form;

    if (!userID) {
      toast.error("Please select a client");
      return;
    }

    if (
      !discount ||
      !fromAmount ||
      !toAmount ||
      !discountType ||
      !orderType
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    const discountValue = Number(discount);
    const fromAmountValue = Number(fromAmount);
    const toAmountValue = Number(toAmount);
    const maxDiscountValue = Number(maxDiscount || 0);

    if (
      !Number.isFinite(discountValue) ||
      discountValue <= 0
    ) {
      toast.error("Enter a valid discount");
      return;
    }

    if (
      !Number.isFinite(fromAmountValue) ||
      fromAmountValue < 0
    ) {
      toast.error("Enter a valid from amount");
      return;
    }

    if (
      !Number.isFinite(toAmountValue) ||
      toAmountValue <= 0
    ) {
      toast.error("Enter a valid to amount");
      return;
    }

    if (toAmountValue < fromAmountValue) {
      toast.error("To amount cannot be less than from amount");
      return;
    }

    if (
      maxDiscount &&
      (!Number.isFinite(maxDiscountValue) ||
        maxDiscountValue < 0)
    ) {
      toast.error("Enter a valid maximum discount");
      return;
    }

    if (
      discountType === "percentage" &&
      discountValue > 100
    ) {
      toast.error("Percentage discount cannot exceed 100%");
      return;
    }

    setLoading(true);

    try {
      const result = await updateDiscount({
        userid: userID,
        discount: String(discountValue),
        fromAmount: String(fromAmountValue),
        toAmount: String(toAmountValue),
        discountType,
        orderType,
        maxDiscount: String(maxDiscountValue),
      });

      if (result?.success) {
        toast.success(
          result?.message || "Discount updated successfully",
        );

        setForm({
          discount: "",
          fromAmount: "",
          toAmount: "",
          discountType: "",
          orderType: "",
          maxDiscount: "",
        });

        await load();
      } else {
        toast.error(
          result?.message || "Failed to update discount",
        );
      }
    } catch (error) {
      console.error("DISCOUNT SAVE ERROR:", error);
      toast.error("Failed to update discount");
    } finally {
      setLoading(false);
    }
  };

  const removeRow = (discountId) => {
    if (!userID) {
      toast.error("Please select a client");
      return;
    }

    ask(
      "Remove discount?",
      "This action cannot be undone.",
      async () => {
        setLoading(true);

        try {
          const result = await deleteDiscount({
            userid: userID,
            discountId,
          });

          if (result?.success) {
            toast.success(
              result?.message || "Discount removed successfully",
            );

            await load();
          } else {
            toast.error(
              result?.message || "Failed to remove discount",
            );
          }
        } catch (error) {
          console.error("DELETE DISCOUNT ERROR:", error);
          toast.error("Failed to remove discount");
        } finally {
          setLoading(false);
        }
      },
    );
  };

  useEffect(() => {
    load();
  }, [userID]);

  return (
    <>
      {loading && list.length === 0 && (
        <SectionCard
          icon={Percent}
          title="Currently Applied Discounts"
        >
          <div className="flex items-center justify-center py-8 text-sm text-slate-500">
            Loading discounts...
          </div>
        </SectionCard>
      )}

      {!loading && userID && list.length === 0 && (
        <SectionCard
          icon={Percent}
          title="Currently Applied Discounts"
        >
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center">
            <p className="text-sm font-medium text-slate-700">
              No discounts configured
            </p>

            <p className="mt-1 text-xs text-slate-500">
              Add a discount using the form below.
            </p>
          </div>
        </SectionCard>
      )}

      {list.map((discountItem) => (
        <SectionCard
          key={discountItem.id}
          icon={Percent}
          title="Currently Applied Discount"
          right={
            <button
              type="button"
              onClick={() => removeRow(discountItem.id)}
              disabled={loading}
              className="rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Remove
            </button>
          }
        >
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            <div>
              <Label>Discount</Label>

              <div className="text-sm font-medium text-slate-800">
                {discountItem.discount_type === "fixed"
                  ? formatCurrency(discountItem.discount)
                  : `${Number(
                      discountItem.discount || 0,
                    ).toFixed(2)}%`}
              </div>
            </div>

            <div>
              <Label>Type</Label>

              <div className="text-sm font-medium capitalize text-slate-800">
                {discountItem.discount_type || "—"}
              </div>
            </div>

            <div>
              <Label>From</Label>

              <div className="text-sm font-medium text-slate-800">
                {formatCurrency(discountItem.from_amt)}
              </div>
            </div>

            <div>
              <Label>To</Label>

              <div className="text-sm font-medium text-slate-800">
                {formatCurrency(discountItem.to_amt)}
              </div>
            </div>

            <div>
              <Label>Max Discount</Label>

              <div className="text-sm font-medium text-slate-800">
                {formatCurrency(discountItem.max_discount)}
              </div>
            </div>

            <div>
              <Label>Order Type</Label>

              <div className="text-sm font-medium text-slate-800">
                {formatOrderType(discountItem.order_type)}
              </div>
            </div>
          </div>
        </SectionCard>
      ))}

      <SectionCard icon={Percent} title="Update Discount">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <div>
            <Label>Discount</Label>

            <Input
              type="number"
              min="0"
              step="0.01"
              value={form.discount}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  discount: event.target.value,
                }))
              }
              placeholder="20"
            />
          </div>

          <div>
            <Label>From Amount</Label>

            <Input
              type="number"
              min="0"
              step="0.01"
              value={form.fromAmount}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  fromAmount: event.target.value,
                }))
              }
              placeholder="1"
            />
          </div>

          <div>
            <Label>To Amount</Label>

            <Input
              type="number"
              min="0"
              step="0.01"
              value={form.toAmount}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  toAmount: event.target.value,
                }))
              }
              placeholder="10000"
            />
          </div>

          <div>
            <Label>Max Discount</Label>

            <Input
              type="number"
              min="0"
              step="0.01"
              value={form.maxDiscount}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  maxDiscount: event.target.value,
                }))
              }
              placeholder="1000"
            />
          </div>

          <div>
            <Label>Discount Type</Label>

            <Select
              value={form.discountType}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  discountType: event.target.value,
                }))
              }
            >
              <option value="">Select</option>
              <option value="fixed">Fixed</option>
              <option value="percentage">Percentage</option>
            </Select>
          </div>

          <div>
            <Label>Order Type</Label>

            <Select
              value={form.orderType}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  orderType: event.target.value,
                }))
              }
            >
              <option value="">Select</option>
              <option value="home_delivery">
                Home Delivery
              </option>
              <option value="takeaway">Takeaway</option>
              <option value="dining">Dining</option>
            </Select>
          </div>
        </div>

        <div className="mt-4">
          <button
            type="button"
            onClick={save}
            disabled={loading || !userID}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save Discount"}
          </button>
        </div>
      </SectionCard>

      <ConfirmDialog
        {...confirm}
        onCancel={cancelConfirm}
      />
    </>
  );
}